import React, { useEffect, useMemo, useState } from "react";
import FindCaseStepper from "../../components/crm/FindCaseStepper";
import {
  FileText,
  Search,
  X,
  RefreshCw,
  Download,
  ChevronRight,
  ChevronLeft,
  Files,
  CircleCheck,
} from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/fetchCaseApi";
import { exportSingleCourtOrder } from "./highCourtOrderFiles";

const CASE_COLLECTIONS = ["pending", "disposed", "decided"];

// Registration-number form used as the case key: "<type>/<reg_no>/<reg_year>".
const buildCaseNo = (order) =>
  order?.type_name && order?.reg_no && order?.reg_year
    ? `${order.type_name}/${order.reg_no}/${order.reg_year}`
    : order?.case_no || "";

// The stored Registration Number varies in punctuation/spacing — exported docs
// look like "WP /6359/2026", "FAPL /188/2020", "CRL.M.C. /2227247/2024" — while
// the order record gives "WP/6359/2026". Normalize to alphanumerics (uppercase)
// so the fallback match is resilient to spaces and dots.
const normalizeCaseNo = (s) => String(s || "").toUpperCase().replace(/[^A-Z0-9]/g, "");

// Court Orders results are order/judgement records (NOT case-party records), so
// they have their own columns: Case Number, Type, Order Date, Document + a
// "View Order" action that streams the PDF via the backend proxy. Unlike Case
// Status, the portal does not return petitioner/respondent names in this payload.
function CourtOrdersListScreen() {
  const location = useLocation();
  const navigate = useNavigate();

  const { globalUser } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [loadingKey, setLoadingKey] = useState(null);
  const [exportingKey, setExportingKey] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const ordersPerPage = 10;

  const responseData = location.state?.data || null;
  const searchParams = location.state?.searchParams || {};

  // The proxied response shape mirrors the search endpoints: { data: { con }, cookies, sessionID }
  const orders = responseData?.data?.con || [];

  // Session needed to fetch the order PDF (portal gates it behind the session
  // that ran the search). HCSERVICES_SESSID rotates per response; JSESSION is
  // stable from the original captcha and lives in the search params.
  const hcservicesSessid =
    responseData?.cookies?.HCSERVICES_SESSID ||
    searchParams?.cookies?.HCSERVICES_SESSID ||
    "";
  const jsessionValue =
    searchParams?.cookies?.JSESSION ||
    searchParams?.cookies?.JSESSIONID ||
    responseData?.cookies?.JSESSION ||
    "";
  const sessionId =
    hcservicesSessid || responseData?.sessionID || searchParams?.sessionId || "";

  useEffect(() => {
    if (!location.state) {
      navigate("/portal/fetchfromhighcourt", { replace: true });
    }
  }, [location.state, navigate]);

  const orderKey = (order, idx) =>
    `${order.cino || ""}__${order.case_no || ""}__${order.order_no ?? idx}`;

  const filteredOrders = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return orders;
    return orders.filter((o) =>
      [o.case_no, o.cino, o.type_name, o.docu_name, o.order_dt]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [orders, searchQuery]);

  // Orders carrying a PDF path — the only ones View/Export can act on.
  const downloadableCount = useMemo(
    () => orders.filter((o) => o.orderurlpath).length,
    [orders]
  );

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setCurrentPage(1);
  };

  const handleViewOrder = async (order, idx) => {
    if (!order.orderurlpath) {
      toast.error("This order has no downloadable document.");
      return;
    }
    // display_pdf.php's caseno is "<type>/<reg_no>/<reg_year>", e.g. "CRLA/4844/2026".
    const caseno =
      order.type_name && order.reg_no && order.reg_year
        ? `${order.type_name}/${order.reg_no}/${order.reg_year}`
        : "";
    const key = orderKey(order, idx);
    setLoadingKey(key);
    try {
      const blob = await apiRequest("court-orders/pdf", {
        orderurlpath: order.orderurlpath,
        caseno,
        cCode: searchParams?.court_code || searchParams?.court_complex_code || "",
        cino: order.cino,
        state_code: searchParams?.state_code || "",
        flag: "nojudgement",
        hcservices_sessid: hcservicesSessid,
        jsession_value: jsessionValue,
        sessionId,
      });
      // A non-PDF blob means the proxy returned a JSON error (e.g. expired
      // session) — read and surface it instead of opening a broken viewer.
      if (!blob || blob.size === 0) {
        toast.error("No order PDF available from the portal.");
        return;
      }
      if (blob.type && blob.type.includes("application/json")) {
        const text = await blob.text();
        let msg = "No order PDF available from the portal.";
        try { msg = JSON.parse(text)?.error || msg; } catch (_) {}
        toast.error(msg, { duration: 5000 });
        return;
      }
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      // Revoke after a delay so the new tab has time to load it.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      // axios threw on a 4xx/5xx — the error body is a Blob for responseType:blob.
      let msg = err?.message || "Failed to open order PDF";
      const data = err?.response?.data;
      if (data instanceof Blob) {
        try { msg = JSON.parse(await data.text())?.error || msg; } catch (_) {}
      } else if (data?.error) {
        msg = data.error;
      }
      toast.error(msg, { duration: 5000 });
    } finally {
      setLoadingKey(null);
    }
  };

  // Find the user's already-exported case that this order belongs to. We match
  // on cino (stored on newer exports) OR the constructed registration number
  // (caseNo). Query owner-only (a single-field index that already exists) and
  // filter client-side, so no new Firestore composite index is required.
  const findExportedCase = async (order) => {
    const uid = globalUser?.uid;
    if (!uid) return null;
    const targetCino = order.cino || "";
    const targetCaseNo = normalizeCaseNo(buildCaseNo(order));
    for (const col of CASE_COLLECTIONS) {
      const snap = await getDocs(
        query(collection(db, col), where("owner", "==", uid))
      );
      const match = snap.docs.find((d) => {
        const c = d.data();
        return (
          (targetCino && c.cino === targetCino) ||
          (targetCaseNo && normalizeCaseNo(c.caseNo) === targetCaseNo)
        );
      });
      if (match) return { id: match.id, ...match.data(), __collection: col };
    }
    return null;
  };

  const handleExportOrder = async (order, idx) => {
    if (!globalUser?.uid) {
      toast.error("Please sign in to export orders.");
      return;
    }
    if (!order.orderurlpath) {
      toast.error("This order has no downloadable document.");
      return;
    }
    const key = orderKey(order, idx);
    setExportingKey(key);
    try {
      const caseDoc = await findExportedCase(order);
      if (!caseDoc) {
        toast.error(
          `Please export the original case first (Case No: ${buildCaseNo(order) || "—"}, CINO: ${order.cino || "—"}) to export this order.`,
          { duration: 7000 }
        );
        return;
      }
      const result = await exportSingleCourtOrder({
        order,
        caseDoc,
        caseCollection: caseDoc.__collection,
        ownerId: globalUser.uid,
        cookies: { HCSERVICES_SESSID: hcservicesSessid, JSESSION: jsessionValue },
        courtCode: searchParams?.court_code || searchParams?.court_complex_code || "",
        stateCode: searchParams?.state_code || "",
      });
      if (result.status === "skipped") {
        toast("Order already exported to this case.", { icon: "ℹ️", duration: 5000 });
      } else if (result.status === "no-pdf") {
        toast.error("Order PDF unavailable from the portal — re-run the search and try again.", { duration: 6000 });
      } else {
        toast.success(`Order exported to case ${caseDoc.caseNo || ""}.`, { duration: 5000 });
      }
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || "Failed to export order";
      toast.error(msg, { duration: 6000 });
    } finally {
      setExportingKey(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / ordersPerPage));
  const page = Math.min(currentPage, totalPages);
  const indexOfLast = page * ordersPerPage;
  const indexOfFirst = indexOfLast - ordersPerPage;
  const pageOrders = filteredOrders.slice(indexOfFirst, indexOfLast);

  const startPage = Math.max(1, Math.min(page - 2, totalPages - 4));
  const pageNumbers = [];
  for (let i = startPage; i <= Math.min(totalPages, startPage + 4); i++) pageNumbers.push(i);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <main className="px-4 sm:px-6 lg:px-8 py-4">
        {/* Breadcrumb */}
        <div className="flex items-center gap-1.5 text-sm">
          <Link to="/portal/findCasesOptions" className="text-slate-500 hover:text-slate-800">Find Cases</Link>
          <ChevronRight className="h-4 w-4 text-slate-400" />
          <Link to="/portal/fetchfromhighcourt" className="text-slate-500 hover:text-slate-800">High Court</Link>
          <ChevronRight className="h-4 w-4 text-slate-400" />
          <span className="font-medium text-slate-800">Court Orders</span>
        </div>

        <div className="mt-4"><FindCaseStepper current={3} /></div>

        {/* Summary stat cards */}
        {orders.length > 0 && (
          <div className="mt-4 grid grid-cols-2 gap-4">
            {[
              { label: "Total Orders", value: orders.length, Icon: Files, tint: "bg-indigo-50 text-indigo-500" },
              { label: "With Document", value: downloadableCount, Icon: CircleCheck, tint: "bg-green-50 text-green-500" },
            ].map(({ label, value, Icon, tint }) => (
              <div
                key={label}
                className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
              >
                <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tint}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <p className="text-2xl font-bold leading-tight text-slate-900">{value}</p>
                  <p className="truncate text-xs text-slate-500">{label}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-4 mb-8 w-full overflow-hidden rounded-2xl border border-slate-200 bg-white text-slate-800 shadow-sm">
          {orders.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-base font-medium text-slate-700">No court orders found</p>
              <button
                type="button"
                onClick={() => navigate("/portal/fetchfromhighcourt")}
                className="mt-2 text-sm font-medium text-yellow-600 hover:underline"
              >
                Back to search
              </button>
            </div>
          ) : (
            <>
              {/* Toolbar */}
              <div className="flex flex-col gap-3 border-b border-slate-100 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="relative w-full max-w-sm">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    placeholder="Search in results..."
                    className="h-10 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-9 text-sm text-slate-900 placeholder-slate-400 outline-none transition-colors focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/30"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={handleClearSearch}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700"
                      title="Clear search"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>

              {filteredOrders.length === 0 ? (
                <div className="py-12 text-center">
                  <p className="text-base font-medium text-slate-700">No orders match your search</p>
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className="mt-2 text-sm font-medium text-yellow-600 hover:underline"
                  >
                    Clear search
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="data-table w-full border-collapse">
                    <thead>
                      <tr className="border-b border-slate-200 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        <th className="w-12 px-4 py-3">#</th>
                        <th className="px-4 py-3">Case Number</th>
                        <th className="px-4 py-3">Type</th>
                        <th className="px-4 py-3">Order Date</th>
                        <th className="px-4 py-3">Document</th>
                        <th className="px-4 py-3">CNR</th>
                        <th className="w-24 px-4 py-3 text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageOrders.map((order, i) => {
                        const idx = indexOfFirst + i;
                        const key = orderKey(order, idx);
                        const isLoading = loadingKey === key;
                        const isExporting = exportingKey === key;
                        const hasDoc = Boolean(order.orderurlpath);
                        return (
                          <tr
                            key={key}
                            className="border-b border-slate-100 transition-colors duration-150 hover:bg-slate-50"
                          >
                            <td className="px-4 py-3.5 text-sm text-slate-500">{order.order_no ?? idx + 1}</td>
                            <td className="px-4 py-3.5 text-sm font-medium text-slate-900">{order.case_no || "—"}</td>
                            <td className="px-4 py-3.5 text-sm text-slate-600">{order.type_name || "—"}</td>
                            <td className="px-4 py-3.5 text-sm text-slate-600">
                              {order.order_dt || order.date_of_decision || "—"}
                            </td>
                            <td className="px-4 py-3.5 text-sm text-slate-600">{(order.docu_name || "Order").trim()}</td>
                            <td className="px-4 py-3.5 text-xs text-slate-500">{order.cino || "—"}</td>
                            <td className="px-4 py-3.5">
                              <div className="flex items-center justify-center gap-1">
                                <button
                                  type="button"
                                  onClick={() => handleViewOrder(order, idx)}
                                  disabled={isLoading || !hasDoc}
                                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
                                  title={hasDoc ? "View order PDF" : "No document available"}
                                >
                                  {isLoading ? (
                                    <RefreshCw className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <FileText className="h-4 w-4" />
                                  )}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleExportOrder(order, idx)}
                                  disabled={isExporting || !hasDoc}
                                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
                                  title="Export this order into its already-exported case"
                                >
                                  {isExporting ? (
                                    <RefreshCw className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Download className="h-4 w-4" />
                                  )}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Footer — result range + pagination */}
              {filteredOrders.length > 0 && (
                <div className="flex flex-col items-center gap-4 border-t border-slate-100 px-4 py-4 sm:flex-row sm:justify-between">
                  <p className="text-sm text-slate-500">
                    Showing {indexOfFirst + 1} to {Math.min(indexOfLast, filteredOrders.length)} of{" "}
                    {filteredOrders.length} results
                  </p>

                  {totalPages > 1 && (
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => setCurrentPage(Math.max(1, page - 1))}
                        disabled={page === 1}
                        className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                        title="Previous page"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>

                      {startPage > 1 && (
                        <>
                          <button
                            onClick={() => setCurrentPage(1)}
                            className="h-9 min-w-[2.25rem] rounded-lg border border-slate-200 px-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50"
                          >
                            1
                          </button>
                          {startPage > 2 && <span className="px-1 text-slate-400">...</span>}
                        </>
                      )}

                      {pageNumbers.map((number) => (
                        <button
                          key={number}
                          onClick={() => setCurrentPage(number)}
                          className={`h-9 min-w-[2.25rem] rounded-lg px-2 text-sm font-medium transition-colors ${
                            page === number
                              ? "bg-yellow-500 text-slate-900"
                              : "border border-slate-200 text-slate-600 hover:bg-slate-50"
                          }`}
                        >
                          {number}
                        </button>
                      ))}

                      {pageNumbers[pageNumbers.length - 1] < totalPages && (
                        <>
                          {pageNumbers[pageNumbers.length - 1] < totalPages - 1 && (
                            <span className="px-1 text-slate-400">...</span>
                          )}
                          <button
                            onClick={() => setCurrentPage(totalPages)}
                            className="h-9 min-w-[2.25rem] rounded-lg border border-slate-200 px-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50"
                          >
                            {totalPages}
                          </button>
                        </>
                      )}

                      <button
                        onClick={() => setCurrentPage(Math.min(totalPages, page + 1))}
                        disabled={page === totalPages}
                        className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                        title="Next page"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export default CourtOrdersListScreen;
